import amplitude, { AmplitudeClient, Revenue } from 'amplitude-js';
import { QUIZ_IDS } from 'src/funnels/common/config/funnels';
import sendFbPurchaseEventFromServer from 'src/funnels/common/helpers/sendFbPurchaseEventFromServer';
import { PaymentMode, Total } from 'src/funnels/common/types';
import getPriceValue from 'src/funnels/common/utils/payment/getPriceValue';
import { getOSFull } from 'common/utils/analytics/getOS';
import getUTMs from 'common/utils/analytics/getUTMs';

interface Config {
  eventName: string;
  data?: any;
  userId?: string;
  email?: string;
}

interface PaymentConfig {
  eventName?: string;
  eventData: {
    total: Total;
    order: Record<string, any>;
    quizId: QUIZ_IDS | '';
    userId: string;
  };
}

class Analytics {
  amplitudeAnalytics: AmplitudeClient;

  AMPLITUDE_API_KEY: string | null = null;

  FBQ_API_KEY: string | null = null;

  constructor() {
    this.amplitudeAnalytics = amplitude.getInstance();
  }

  init = (
    amplitudeKey: string | undefined,
    fbqKey: string | undefined,
  ): void => {
    if (!amplitudeKey) {
      console.error("VITE_AMPLITUDE_API_KEY isn't applied");
      return;
    }

    if (!fbqKey) {
      console.error("VITE_FACEBOOK_PIXEL_API_KEY key isn't applied");
      return;
    }

    this.AMPLITUDE_API_KEY = amplitudeKey;
    this.FBQ_API_KEY = fbqKey;
    this.amplitudeAnalytics.init(this.AMPLITUDE_API_KEY);
  };

  initApp = (amplitudeKey: string | undefined): void => {
    if (!amplitudeKey) {
      console.error("VITE_AMPLITUDE_API_KEY isn't applied");
      return;
    }

    this.AMPLITUDE_API_KEY = amplitudeKey;

    this.amplitudeAnalytics.init(this.AMPLITUDE_API_KEY);
  };

  setUserId = (userId: string): void => {
    amplitude.getInstance().setUserId(userId);
  };

  setUserProperties = (props: any): void => {
    amplitude.getInstance().setUserProperties(props);
  };

  setUserUTMs = (queryParams: URLSearchParams, funnelName: string): void => {
    if (import.meta.env.MODE !== 'production') return;

    const UTMs = getUTMs(queryParams);

    this.amplitudeAnalytics.setUserProperties({
      ...UTMs,
      funnel_name: funnelName,
    });
  };

  setUserViewPortAndOs = (): void => {
    const os = getOSFull();

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const userProperties = {
      screen_size: `${windowWidth}x${windowHeight}`,
      os,
    };

    this.setUserProperties(userProperties);
  };

  send = (config: Config): void => {
    const { eventName, data } = config;
    amplitude.getInstance().logEvent(eventName, data);

    if (import.meta.env.MODE !== 'production') return;

    const { fbq } = window as any;

    if (fbq) {
      fbq('trackCustom', eventName, data);
    }
  };

  sendPayment = (
    config: PaymentConfig,
    email: string,
    isDownsellView: boolean,
  ): void => {
    const { eventName = 'sales_payment_success', eventData } = config;
    const { total, quizId, userId, order } = eventData;
    const payload = {
      external_id: userId,
      order_id: order.order_id,
      onboarding_id: quizId,
      subscription_id: order.subscription_id,
      productId: total.productId,
      amount: total.firstPayment,
      currency: 'USD',
      provider: 'solid',
      payment_type: order.payment_type,
      payment_page: isDownsellView === true ? 'downsell' : 'sales',
    };

    this.setUserProperties({ payment_type: order.payment_type });

    const price = total.firstPayment / 100;
    const value = total ? getPriceValue(total) : price;

    const paymentMode = total.products.some(
      (prod) =>
        prod.paymentType === PaymentMode.MANIFEST_SUBSCRIPTION ||
        prod.paymentType === PaymentMode.PDF_SUBSCRIPTION,
    )
      ? 'Subscribe'
      : 'StartTrial';

    const revenue = new Revenue()
      .setQuantity(1)
      .setPrice(value)
      .setRevenueType('purchase')
      .setProductId(total.productId)
      .setEventProperties({ currency: payload.currency });
    amplitude.getInstance().logRevenueV2(revenue);
    amplitude.getInstance().logEvent(eventName, { ...payload, amount: price });

    if (import.meta.env.MODE !== 'production') return;

    const { fbq } = window as any;

    if (fbq) {
      fbq('trackCustom', eventName, payload);
      fbq(
        'track',
        'Purchase',
        {
          value,
          external_id: payload.external_id,
          currency: payload.currency,
          content_ids: [payload.productId],
          content_type: 'product',
        },
        { eventID: payload.order_id },
      );

      fbq(
        'track',
        paymentMode,
        {
          value,
          external_id: payload.external_id,
          subscription_id: payload.subscription_id,
          currency: payload.currency,
          content_ids: [payload.productId],
          content_type: 'product',
        },
        { eventID: payload.order_id },
      );

      sendFbPurchaseEventFromServer({
        currency: payload.currency,
        orderId: order.order_id,
        subscriptionId: order.subscription_id,
        userId,
        email,
        value,
      });
    }
  };

  sendUserEmail = (config: Config): void => {
    const { eventName, data } = config;
    const identify = new amplitude.Identify().add('email', data.email);
    amplitude.getInstance().identify(identify); // send the Identify call

    if (import.meta.env.MODE !== 'production') return;

    const { fbq } = window as any;

    if (fbq) {
      fbq('init', this.FBQ_API_KEY, {
        em: data.email,
        external_id: data.external_id,
      });
    }

    this.completeRegistration({
      eventName,
      data,
    });
  };

  completeRegistration = (config: Config): void => {
    const { eventName, data } = config;

    amplitude.getInstance().logEvent(eventName, data);

    const { fbq } = window as any;

    if (fbq) {
      fbq('trackCustom', eventName, data);
      fbq('track', 'CompleteRegistration', data);
    }
  };

  submitApplication = (config: Config): void => {
    const { eventName, data } = config;
    amplitude.getInstance().logEvent(eventName, data);

    if (import.meta.env.MODE !== 'production') return;

    const { fbq } = window as any;

    if (fbq) {
      fbq('trackCustom', eventName, data);
      fbq('track', 'SubmitApplication', data);
    }
  };

  initiateCheckout = (config: Config): void => {
    const { eventName, data } = config;
    amplitude.getInstance().logEvent(eventName, data);

    if (import.meta.env.MODE !== 'production') return;

    const { fbq } = window as any;

    if (fbq) {
      fbq('trackCustom', eventName, data);
      fbq('track', 'InitiateCheckout', data);
    }
  };

  trackPageView = (config: Config): void => {
    const { data, userId = '' } = config;
    const { location } = data;
    const queryObject: Record<string, any> = {};

    this.setUserId(userId);
    this.setUserProperties(queryObject);
    amplitude.getInstance().logEvent('page_view', {
      page_path: location.pathname + location.search,
    });

    if (import.meta.env.MODE !== 'production') return;

    const { fbq } = window as any;

    if (fbq) {
      fbq('track', 'PageView');
    }
  };
}

export const analytics = new Analytics();
